import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { RJSFSchema } from '@rjsf/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import memoize from 'lodash/memoize';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link as MUILink,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Order } from '@luxuryescapes/contract-svc-order';
import { LUX_LOYALTY_PROGRAM_CONFIG } from '@luxuryescapes/lib-lux-loyalty';

import TenantBrandChip from '~/components/Common/Elements/TenantBrandChip';
import EmployeeUserList from '~/components/Common/EmployeeUserList';
import UpdateUser from '~/components/Common/UpdateUser';
import {
  checkCanRedeemLuxPlusBenefits,
  checkCanViewLuxPlusBenefits,
  hasLuxPlusSubscriptionStatus,
  isFreePreviewLuxPlusMember,
} from '~/components/Membership/utils';
import UserAbandonedCartsTable from '~/components/System/UserAbandonedCartsTable';
import CreatePromoForUserModal from '~/components/Users/CreatePromoForUserModal';
import SendPreCallSmsButton from '~/components/Users/UserDetails/SendPreCallSms/SendPreCallSmsButton';
import UpgradeToLuxPlusButton from '~/components/Users/UserDetails/UpgradeToLuxPlusButton';
import SimpleVoucherForm from '~/components/Vouchers/SimpleVoucherForm';

import { AGENT_HUB_AUTH_ROLE_AFFILIATION_ADMIN } from '~/consts/agentHub';
import { CUDO, DEALS, LE_AGENT_HUB, LE_AGENT_HUB_CVFR, LUXURY_ESCAPES, SCOOPON, TREAT_ME } from '~/consts/brands';
import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';
import { hasBusinessAdminOrManagerRole } from '~/consts/businessTravellerRoles';
import { LUX_PLUS } from '~/consts/membership';
import {
  ROLE_ADMIN_USER,
  ROLE_EMPLOYEE_USER,
  ROLE_EXPERIENCES_COORDINATOR,
  ROLE_ICS_STORE_TEAM,
  ROLE_TOUR_COORDINATOR,
} from '~/consts/roles';
import TENANTS from '~/consts/tenant';

import { useGetAgentHubUser } from '~/hooks/apis/agent/useGetAgentHubUser';
import { useGetEmployee } from '~/hooks/apis/business-traveller/useGetEmployee';
import type { EnrolledTier } from '~/hooks/apis/loyalty/useEnrolLuxLoyaltyAccount';
import { useSetAccountEnabled } from '~/hooks/apis/users/useSetAccountEnabled';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { LanguageOption } from '~/services/LabService';
import UsersService from '~/services/UsersService';

import { EoiStatus } from '~/types/services/agentHub';

import { canEnableDisableAccounts } from '~/utils/adminPermission';
import { isIndependentAgentHubAffiliation } from '~/utils/agentHub';
import { apiToFormBusinessDetails } from '~/utils/businessDetailsMapping';
import canShowTabExceptRoles from '~/utils/canShowTabExceptRoles';
import { isLuxLoyaltyEnabled } from '~/utils/luxLoyalty';
import { isAgentHubSubTenant } from '~/utils/tenantUtils';
import { propertiesToTravelPreferencesForm } from '~/utils/travelPreferencesMapping';

import PermissionedComponent from '../Common/PermissionedComponent';
import UserAgencyDetails from '../LeAgentHub/components/UserAgencyDetails';
import BookingAuthorityOtpButton from '../LuxLoyalty/BookingAuthorityOtpButton';
import LuxLoyaltyAccountChip from '../LuxLoyalty/LuxLoyaltyAccountChip';
import LuxLoyaltyEnrolPanel from '../LuxLoyalty/LuxLoyaltyEnrolPanel';
import LuxLoyaltyUserProfile from '../LuxLoyalty/LuxLoyaltyUserProfile';
import LuxPlusMembershipChip from '../LuxPlus/LuxPlusMembershipChip';
import MembershipSubscriptionDetails from '../Membership/MembershipSubscriptionDetails';
import UserRecentOfferPageViewsTable from '../System/UserRecentOfferPageViewsTable';
import UserRecentSearchTable from '../System/UserRecentSearchTable';
import FraudWarning from '../Users/History/FraudWarning';
import SamePhoneAccounts from '../Users/History/SamePhoneAccounts';
import { withTenant } from '../hoc';

import AtolCertificatesTab from './AtolCertificates/AtolCertificatesTab';
import CartsTab from './Carts/CartsTab';
import CustomOffersTab from './CustomOffers/CustomOffersTab';
import ForceLogoutModal from './ForceLogoutModal';
import FrequentFlyerMembershipsTab from './FrequentFlyerMemberships/FrequentFlyerMembershipsTab';
import GDPRRemovalModal from './GRPRRemovalModal';
import GDPRRequestModal from './GRPRRequestModal';
import QuotesTab from './Quotes/QuotesTab';
import ResetPasswordDialog from './ResetPasswordDialog';
import SimilarAccountsTab from './SimilarAccounts/SimilarAccountsTab';
import AgentHubPortalSpoofButton from './UserDetails/AgentHubPortalSpoofButton';
import BusinessAdminSpoofButton from './UserDetails/BusinessAdminSpoofButton';
import BusinessTravellerBusinessLabel from './UserDetails/BusinessTravellerBusinessLabel';
import CreateCreditSecurePaymentDialog from './UserDetails/CreateCreditSecurePaymentDialog';
import EmailStatusCard from './UserDetails/DeliveryStatusCard';
import ResolveAccountTakeoverInstructions from './UserDetails/ResolveAccountTakeoverInstructions';
import SecurityTab from './UserDetails/SecurityTab';
import SpoofUserBlock from './UserDetails/SpoofUserBlock';
import UserAccountHistory from './UserDetails/UserAccountHistory';
import UserDetailsNavigation from './UserDetails/UserDetailsNavigation';
import VelocityMembershipsTab from './UserDetails/VelocityMembershipsTab';
import VerifyCustomerButtons from './UserDetails/VerifyCustomer/VerifyCustomerButtons';
import { UserDetailsTabKey as TabKey, UserDetailsTabs, isUserDetailsTabKey } from './UserDetails/userDetailsTabs';
import { validUserTenantForSiteTenant } from './UserDetails/utils';

const getRegionsByCountryCode = memoize((regions) => {
  return regions.reduce((regionsByCountryCode, region) => {
    regionsByCountryCode[region.code] = region;
    return regionsByCountryCode;
  }, {});
});

function getTabFromUrlParams(searchParams: URLSearchParams): TabKey {
  const value = searchParams.get('tab');
  return isUserDetailsTabKey(value) ? value : 'user-details';
}

const actionButtonSx = {
  borderRadius: 999,
  minHeight: 32,
  px: 2,
  py: 0.5,
  textTransform: 'none',
  fontWeight: 500,
  color: 'grey.900',
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
  borderColor: 'transparent',
  '&:hover': (theme) => ({
    bgcolor: alpha(theme.palette.primary.main, 0.16),
    borderColor: 'transparent',
  }),
};

const destructiveButtonSx = {
  borderRadius: 999,
  minHeight: 32,
  py: 0.5,
  textTransform: 'none',
  fontWeight: 700,
  borderColor: 'error.light',
};

const neutralDangerButtonSx = {
  ...destructiveButtonSx,
  color: 'text.primary',
  borderColor: 'grey.500',
  '&:hover': {
    borderColor: 'grey.700',
    bgcolor: 'action.hover',
  },
};

const spoofButtonSx = {
  borderRadius: 999,
  minHeight: 32,
  px: 2,
  py: 0.5,
  textTransform: 'none',
  fontWeight: 700,
};

const primaryActionButtonSx = {
  ...actionButtonSx,
  fontWeight: 700,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  '&:hover': { bgcolor: 'primary.dark' },
};

const quickActionLabelSx = {
  color: 'text.secondary',
  fontWeight: 600,
  textAlign: 'left',
};

const quickActionRowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '130px minmax(0, 1fr)' },
  gap: { xs: 1, md: 2 },
  alignItems: 'center',
};

export type UserTenancy = { id: string; tenant: string }; // id is the id of the user

type Props = {
  user: App.User;
  isAdmin: boolean;
  tenant: App.Tenant;
  tenancy: Array<UserTenancy>;
  schema: { put: RJSFSchema };
  regions: Array<App.Region>;
  updatePreferredAgent: (agent: string) => void;
  update: (user: App.User) => void;
  subscriptions: App.MembershipSubscriptions | null;
  luxLoyaltyAccount: App.LuxLoyaltyAccount | null;
  languageOptions: Array<LanguageOption>;
  currentSubscriptionOrderItem: Order.SubscriptionItem;
  currentSubscriptionOrder: App.Order;
  refreshData: () => Promise<void>;
  context?: { user: any };
};

const newCustomOfferUri = (user: App.User, offerType = 'cruise') => {
  return `/users/${user.id_member}/custom-offers/create?${offerType}=true`;
};

const historyUri = (user: App.User) => {
  return `/users/${user.id_member}/history`;
};

const lesaChatsUri = (user: App.User) => {
  return `/support-assistant/chats-history?search=${encodeURIComponent(user.id_member)}`;
};

function UserDetails(props: Props) {
  const { push, replace } = useAppNavigation();
  const location = useLocation();
  const UpdateUserFormRef = useRef<UpdateUser>(null);
  const detailsPanelRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { user: currentUser } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();

  const {
    user,
    isAdmin,
    tenant,
    tenancy,
    schema,
    regions,
    updatePreferredAgent,
    update,
    subscriptions,
    luxLoyaltyAccount,
    languageOptions,
    currentSubscriptionOrderItem,
    currentSubscriptionOrder,
    refreshData,
  } = props;

  const canToggleAccountStatus = canEnableDisableAccounts(currentUser);
  const accountEnabled = user.status === 'ENABLED';
  const { mutate: setAccountEnabled, isPending: isTogglingAccountStatus } = useSetAccountEnabled(user.id_member);
  const [isAccountStatusDialogOpen, setIsAccountStatusDialogOpen] = useState(false);

  useEffect(() => setIsAccountStatusDialogOpen(false), [user.id_member]);

  const toggleAccountStatus = () => {
    if (!canToggleAccountStatus) return;
    setAccountEnabled(!accountEnabled, {
      onSuccess: () => {
        setIsAccountStatusDialogOpen(false);
        window.location.reload();
      },
      onError: (error) => enqueueSnackbar(`Unable to update account status. ${error.message}`, { variant: 'error' }),
    });
  };

  const { data: businessDetailsResult } = useQuery({
    queryKey: ['users', user.id_member, 'businessDetails'],
    queryFn: ({ signal }) => UsersService.getUserBusinessDetails(user.id_member, { signal }),
  });

  const { data: travelPreferencesResult, isLoading: isTravelPreferencesLoading } = useQuery({
    queryKey: ['users', user.id_member, 'travelPreferences'],
    queryFn: ({ signal }) => UsersService.getCustomerTravelPreferences(user.id_member, { signal }),
  });

  const { data: employee } = useGetEmployee(user.id_member, tenant.brand === LE_BUSINESS_TRAVELLER);
  const isAgentHubTenant = isAgentHubSubTenant(tenant);
  const {
    data: agentHubUser,
    isPending: isAgentHubUserPending,
    isError: isAgentHubUserError,
  } = useGetAgentHubUser(user.id_member, tenant.brand, { enabled: isAgentHubTenant });
  // The agent portal only lets onboarded agents in.
  const canSpoofAgentHubPortal = tenant.value === LE_AGENT_HUB && agentHubUser?.status === EoiStatus.Onboarded;

  const initialBusinessDetails = apiToFormBusinessDetails(businessDetailsResult);
  const handleBusinessDetailsSaved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['users', user.id_member, 'businessDetails'] });
  }, [queryClient, user.id_member]);

  const initialTravelPreferences = useMemo(
    () => propertiesToTravelPreferencesForm(travelPreferencesResult),
    [travelPreferencesResult],
  );

  const handleTravelPreferencesSaved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['users', user.id_member, 'travelPreferences'] });
  }, [queryClient, user.id_member]);

  const urlSearchParams = new URLSearchParams(location.search);
  const showLuxPlusTabInitially =
    urlSearchParams.get('view_luxplus_membership') === 'true' && checkCanViewLuxPlusBenefits(subscriptions);

  const [offer_id, setOfferId] = useState('');
  const [isCreatePromoCodeModalVisible, setIsCreatePromoCodeModalVisible] = useState(false);
  const [isQuickVoucherDialogOpen, setIsQuickVoucherDialogOpen] = useState(false);
  const [isGDPRRemovalModalVisible, setIsGDPRRemovalModalVisible] = useState(false);
  const [isGDPRRequestModalVisible, setIsGDPRRequestModalVisible] = useState(false);
  const [isForceLogoutModalVisible, setIsForceLogoutModalVisible] = useState(false);
  const [isResetPasswordModalVisible, setIsResetPasswordModalVisible] = useState(false);
  const [isEmailStatusDialogOpen, setIsEmailStatusDialogOpen] = useState(false);
  const [showFlightsWarningModal, setShowFlightsWarningModal] = useState(false);
  const [isCreditSecurePaymentDialogOpen, setIsCreditSecurePaymentDialogOpen] = useState(false);
  const [justEnrolledTier, setJustEnrolledTier] = useState<EnrolledTier | null>(null);

  const [currentTab, setCurrentTab] = useState<TabKey>(
    showLuxPlusTabInitially ? 'luxplus-member-profile' : getTabFromUrlParams(urlSearchParams),
  );
  const [pendingTab, setPendingTab] = useState<TabKey | null>(null);

  const [hasBusinessAdminSpoofRoles, setHasBusinessAdminSpoofRoles] = useState<boolean>(false);

  const changeCurrentTab = (tab: TabKey) => {
    if (currentTab === 'luxplus-member-profile') {
      clearSearchParams();
    }
    setCurrentTab(tab);
    requestAnimationFrame(() => {
      if (detailsPanelRef.current?.getBoundingClientRect().top < 0) {
        detailsPanelRef.current.scrollIntoView({ block: 'start' });
      }
    });
  };

  const setCurrentTabHandler = (tab: TabKey) => {
    if (tab === currentTab) {
      return;
    }
    if (currentTab === 'user-details' && UpdateUserFormRef.current?.hasUnsavedChanges) {
      setPendingTab(tab);
      return;
    }
    changeCurrentTab(tab);
  };

  const buyPageUri = (offer_id: string) => {
    return '/offers/' + offer_id + '/buy_for/' + user.id_member;
  };

  const purchasePageUri = () => {
    return `/purchases/section/allOrders?customer_id=${user.id_member}`;
  };

  const creditsPageUri = (userCountry) => {
    if (!userCountry) {
      return '#';
    }

    return `/users/${user.id_member}/credits?currency=${userCountry.currencyCode}`;
  };

  const promoRequestPageUri = () => {
    return `/marketing/promo-requests?userId=${user.id_member}&page=0&limit=100`;
  };

  const voucherLookupPageUri = () => {
    return `/marketing/vouchers?user_id=${encodeURIComponent(user.id_member)}#voucher-users`;
  };

  const referralLogPageUri = () => {
    return `/marketing/referralLogs?q=${user.id_member}`;
  };

  const newOrderPageUri = () => {
    return `/users/${user.id_member}/new-order/offer-selection`;
  };

  const newFlightPageUri = () => {
    return `/users/${user.id_member}/new-flight-order/traveller`;
  };

  const newGiftCardOrderPageUri = () => {
    return `/users/${user.id_member}/new-gift-card-order/gift-card-selection`;
  };

  const newInsuranceOrderPageUri = () => {
    return `/users/${user.id_member}/new-insurance-order/insurance`;
  };

  const newAtolPageUri = () => {
    return `/users/${user.id_member}/atol`;
  };

  const newSentEmailUri = () => {
    return `/email/sent?email=${encodeURIComponent(user.email)}`;
  };

  const newMarketingEmailsUri = () => {
    return `/customer-communication/email/sent?userId=${encodeURIComponent(user.id_member)}`;
  };

  const handleBuyOfferChange = (event) => {
    setOfferId(event.target.value);
  };

  const handleBuyOfferSubmit = (event) => {
    event.preventDefault();
    push(buyPageUri(offer_id));
  };

  const updateUser = () => {
    UpdateUserFormRef.current?.submitButton.click();
  };

  const gdprRemoval = () => {
    setIsGDPRRemovalModalVisible(true);
  };

  const gdprRequest = () => {
    setIsGDPRRequestModalVisible(true);
  };

  const hideGDPRRemovalModal = () => {
    setIsGDPRRemovalModalVisible(false);
  };

  const hideGDPRRequestModal = () => {
    setIsGDPRRequestModalVisible(false);
  };

  const showCreatePromoCodeModal = () => {
    setIsCreatePromoCodeModalVisible(true);
  };

  const showForceLogoutModal = () => {
    setIsForceLogoutModalVisible(true);
  };

  const showResetPasswordModal = () => {
    setIsResetPasswordModalVisible(true);
  };

  const hideResetPasswordModal = () => {
    setIsResetPasswordModalVisible(false);
  };

  const hideCreatePromoCodeModal = () => {
    setIsCreatePromoCodeModalVisible(false);
  };

  const showQuickVoucherDialog = () => {
    setIsQuickVoucherDialogOpen(true);
  };

  const hideQuickVoucherDialog = () => {
    setIsQuickVoucherDialogOpen(false);
  };

  const showCreditSecurePaymentDialog = () => {
    setIsCreditSecurePaymentDialogOpen(true);
  };

  const hideCreditSecurePaymentDialog = () => {
    setIsCreditSecurePaymentDialogOpen(false);
  };

  const hideForceLogoutModal = () => {
    setIsForceLogoutModalVisible(false);
  };

  const showEmailStatusDialog = () => {
    setIsEmailStatusDialogOpen(true);
  };

  const hideEmailStatusDialog = () => {
    setIsEmailStatusDialogOpen(false);
  };

  const showSpoofUserBlock = (tenant, user, userTenancies) => {
    if (tenant.value === TENANTS.scoopon.value) {
      const scooponUserId = userTenancies.find((el) => el.tenant === TENANTS.scoopon.value)?.id;
      return user.id_member === scooponUserId;
    }

    return validUserTenantForSiteTenant(tenant.value, user.tenant);
  };

  const getFullTitle = () => {
    const shadowBanned = user.shadow_ban_user ? ' Shadow banned' : '';
    const vipClient = user.vip_client ? ' (VIP Client)' : '';
    const hasDash = shadowBanned || vipClient ? ' -' : '';

    return user.fullName + hasDash + shadowBanned + vipClient;
  };

  const luxPlusFreePreview = tenant.brand !== LE_BUSINESS_TRAVELLER && isFreePreviewLuxPlusMember(subscriptions);
  const canViewLuxPlusBenefits = tenant.brand !== LE_BUSINESS_TRAVELLER && checkCanViewLuxPlusBenefits(subscriptions);
  const hasLuxLoyaltyAccount = !!luxLoyaltyAccount;
  const infiniteBookingAuthorities = luxLoyaltyAccount?.infiniteProfile?.bookingAuthorities ?? [];
  const canVerifyBookingAuthoritySms = luxLoyaltyAccount?.tier === 'infinite' && !!luxLoyaltyAccount.infiniteProfile;

  const getSubtitle = () => {
    const content = [];

    if (canViewLuxPlusBenefits) {
      content.push(<LuxPlusMembershipChip type={luxPlusFreePreview ? 'free-preview' : 'default'} size="medium" />);
    }

    if (isLuxLoyaltyEnabled() && hasLuxLoyaltyAccount) {
      const canOpenSocieteProfile = canShowTabExceptRoles(user, [ROLE_EXPERIENCES_COORDINATOR]);
      content.push(
        <LuxLoyaltyAccountChip
          luxLoyaltyAccount={luxLoyaltyAccount}
          size="medium"
          onClick={canOpenSocieteProfile ? () => setCurrentTabHandler('luxloyalty-member-profile') : undefined}
        />,
      );
    }

    if (tenant.brand === 'lebusinesstraveller') {
      content.push(<BusinessTravellerBusinessLabel customer={user} />);
    }

    return (
      content.length > 0 && (
        <Stack direction="row" gap={2} alignItems="center">
          {content.map((item, idx) => (
            <Fragment key={idx}>{item}</Fragment>
          ))}
        </Stack>
      )
    );
  };

  const clearSearchParams = () => {
    replace({
      search: '',
    });
  };

  useEffect(() => {
    // Add the searched user ID and label to recent users
    const saved = localStorage.getItem('recentlyViewedUsers');
    const recentUsers = saved ? JSON.parse(saved) : [];
    const userId = user.id_member;
    const label = `${user.fullName} - ${user.email}`;
    const userEntry = { id: userId, label, date: new Date().toISOString(), tenant: user.tenant };
    const updatedUsers = [userEntry, ...recentUsers.filter((u) => u.id !== userId)].slice(0, 30);
    localStorage.setItem('recentlyViewedUsers', JSON.stringify(updatedUsers));
  }, [user.id_member, user.tenant, tenant.value, regions, user.email, user.fullName]);

  useEffect(() => {
    if (tenant.value !== LE_BUSINESS_TRAVELLER) {
      return;
    }
    if (employee?.roles) {
      setHasBusinessAdminSpoofRoles(hasBusinessAdminOrManagerRole(employee.roles));
    } else if (employee !== undefined) {
      // employee loaded but has no roles — default to false
      setHasBusinessAdminSpoofRoles(false);
    }
  }, [employee, tenant.value]);

  const getLuxPlusLabel = () => {
    return `${checkCanRedeemLuxPlusBenefits(subscriptions) || luxPlusFreePreview ? '' : `${subscriptions?.status} `}${
      luxPlusFreePreview ? 'Free Preview ' : ''
    }${LUX_PLUS.PROGRAM_NAME} Profile`;
  };

  const regionsByCode = getRegionsByCountryCode(regions);
  const userCountry = regionsByCode[user.country_code];
  const fullTitle = getFullTitle();
  const subtitle = getSubtitle();
  const disallowedRoles =
    isAgentHubTenant && (isAgentHubUserPending || isAgentHubUserError || isIndependentAgentHubAffiliation(agentHubUser))
      ? [
          {
            role: AGENT_HUB_AUTH_ROLE_AFFILIATION_ADMIN,
            message: isIndependentAgentHubAffiliation(agentHubUser)
              ? 'Independent agents cannot be assigned the affiliation admin role.'
              : 'Agent Hub agency details must load before assigning the affiliation admin role.',
          },
        ]
      : [];

  const tabs: UserDetailsTabs = {
    'account-history': { label: 'Account & login history', showTab: true },
    'user-details': {
      label: 'User Details',
      showTab: true,
    },
    'loyalty-programs-memberships': {
      label: 'Loyalty Programs Memberships',
      showTab: tenant.brand === LE_BUSINESS_TRAVELLER,
    },
    quotes: {
      label: 'Quotes',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_EXPERIENCES_COORDINATOR, ROLE_ICS_STORE_TEAM]),
    },
    'custom-offers': {
      label: 'Custom offers & quotes',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_EXPERIENCES_COORDINATOR]),
    },
    'luxplus-member-profile': {
      label: getLuxPlusLabel(),
      showTab:
        tenant.brand !== LE_BUSINESS_TRAVELLER &&
        hasLuxPlusSubscriptionStatus(subscriptions) &&
        canShowTabExceptRoles(props.context?.user, [ROLE_EXPERIENCES_COORDINATOR, ROLE_ICS_STORE_TEAM]),
    },
    'luxloyalty-member-profile': {
      label: `${LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName} Profile`,
      showTab: isLuxLoyaltyEnabled() && canShowTabExceptRoles(user, [ROLE_EXPERIENCES_COORDINATOR]),
    },
    'velocity-memberships': {
      label: 'Velocity',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_EXPERIENCES_COORDINATOR, ROLE_ICS_STORE_TEAM]),
    },
    'user-agency': {
      label: 'Agency',
      showTab:
        isAgentHubSubTenant(tenant) && canShowTabExceptRoles(user, [ROLE_EXPERIENCES_COORDINATOR, ROLE_ICS_STORE_TEAM]),
    },
    'recent-searches': {
      label: 'Recent Searches',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_ICS_STORE_TEAM, ROLE_ICS_STORE_TEAM]),
    },
    'recent-offer-page-views': {
      label: 'Recent Offer Page Views',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_ICS_STORE_TEAM]),
    },
    'abandoned-carts': {
      label: 'Abandoned Carts',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_ICS_STORE_TEAM]),
    },
    carts: {
      label: 'Carts',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_ICS_STORE_TEAM]),
    },
    security: {
      label: 'Security',
      showTab: Boolean(currentUser?.roles?.includes(ROLE_ADMIN_USER)),
    },
    'similar-accounts': {
      label: 'Similar Accounts',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_ICS_STORE_TEAM]),
    },
    'atol-certificates': {
      label: 'ATOL Certificates',
      showTab: canShowTabExceptRoles(props.context?.user, [ROLE_ICS_STORE_TEAM]),
    },
  };

  const isTenantOfCurrentSite = validUserTenantForSiteTenant(tenant.value, user.tenant);
  const showSpoofActions = showSpoofUserBlock(tenant, user, tenancy);

  const NON_LEBT_TENANTS = new Set([LUXURY_ESCAPES, LE_AGENT_HUB, LE_AGENT_HUB_CVFR, SCOOPON, CUDO, DEALS, TREAT_ME]);

  // Button visibility configuration per tenant - positive structure
  const BUTTONS_MAP = {
    newPromo: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    promoRequests: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    vouchers: {
      tenants: new Set([LUXURY_ESCAPES]),
    },
    referrals: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    newAtolEmail: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    verifyCustomerSMS: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    createOfferLink: {
      tenants: new Set(NON_LEBT_TENANTS),
    },
    newGiftCard: {
      tenants: new Set([LUXURY_ESCAPES, LE_BUSINESS_TRAVELLER]),
    },
  } as const;

  const shouldShowButton = (buttonKey: keyof typeof BUTTONS_MAP) => {
    const buttonConfig = BUTTONS_MAP[buttonKey];
    return buttonConfig.tenants.has(tenant.brand);
  };

  const showPromptToUseCustomOfferFlights = () => {
    if (['luxuryescapes', 'cudotravel', 'dealstravel', 'treatmetravel'].includes(tenant.brand)) {
      setShowFlightsWarningModal(true);
    } else {
      push(newFlightPageUri());
    }
  };

  return (
    <>
      <Dialog open={showFlightsWarningModal} onClose={() => setShowFlightsWarningModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>We are now using custom offer flights</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            ONLY use offline flights if custom offer flights is not working. Please escalate this to your manager.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose how you would like to proceed:
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="text"
              fullWidth
              component={MUILink}
              href={`/users/${user.id_member}/new-flight-order/traveller`}
              sx={{
                textDecoration: 'underline',
                textUnderlineOffset: '4px',
              }}
            >
              Create Offline Flight
            </Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => push(newCustomOfferUri(user, 'flight'))}
            >
              Create Custom Offer Flight
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setShowFlightsWarningModal(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Helmet>
        <title>Users | {user?.fullName || user.email}</title>
      </Helmet>

      <Box sx={{ pt: 2, pb: 3 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} alignItems={{ xs: 'stretch', lg: 'flex-start' }} gap={2}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: 0 }}>
              {fullTitle}
            </Typography>
            {user.tenant && <TenantBrandChip tenantValue={user.tenant} />}
            {subtitle}
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="flex-end"
            spacing={2}
            flexShrink={0}
          >
            {tenant.brand !== LE_BUSINESS_TRAVELLER && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: { sm: 420, lg: 560 }, flex: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  Preferred Agent:
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <EmployeeUserList
                    user={user}
                    isAdmin={isAdmin}
                    onUpdatePreferredAgent={updatePreferredAgent}
                    brand={tenant.brand}
                  />
                </Box>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>

      <FraudWarning userId={user?.id_member} />

      <ResolveAccountTakeoverInstructions user={user} />

      <Stack direction="column" gap={3}>
        {isTenantOfCurrentSite && !userCountry && (
          <Alert severity="warning">
            <strong>Error!</strong> There is no country set for this user. To view credits you need to add country.
          </Alert>
        )}

        <Card variant="outlined">
          <CardContent sx={{ px: { xs: 2.5, md: 3.5 }, py: 3 }}>
            <Stack spacing={1.5}>
              <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
                <Box sx={quickActionRowSx}>
                  <Typography variant="body2" sx={quickActionLabelSx}>
                    Spoof
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {showSpoofActions && (
                      <SpoofUserBlock
                        user={user}
                        tenant={tenant}
                        buttonVariant="contained"
                        buttonSx={spoofButtonSx}
                        containerSpacing={0}
                        containerSx={{ display: 'contents' }}
                      />
                    )}
                    {tenant.value === LE_BUSINESS_TRAVELLER && showSpoofActions && hasBusinessAdminSpoofRoles && (
                      <BusinessAdminSpoofButton user={user} buttonSx={spoofButtonSx} />
                    )}
                    {showSpoofActions && canSpoofAgentHubPortal && (
                      <AgentHubPortalSpoofButton user={user} buttonSx={spoofButtonSx} />
                    )}
                    {(isTenantOfCurrentSite && shouldShowButton('verifyCustomerSMS')) || showSpoofActions ? (
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderRightWidth: 2 }} />
                    ) : null}
                    {isTenantOfCurrentSite && (
                      <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
                        {shouldShowButton('verifyCustomerSMS') && (
                          <VerifyCustomerButtons
                            user={user}
                            buttonVariant="contained"
                            buttonSx={primaryActionButtonSx}
                          />
                        )}
                      </PermissionedComponent>
                    )}
                    {isTenantOfCurrentSite && (
                      <SendPreCallSmsButton user={user} buttonVariant="contained" buttonSx={primaryActionButtonSx} />
                    )}
                    {canVerifyBookingAuthoritySms && (
                      <BookingAuthorityOtpButton
                        bookingAuthorities={infiniteBookingAuthorities}
                        buttonSx={primaryActionButtonSx}
                      />
                    )}
                  </Stack>
                </Box>
              </PermissionedComponent>

              {isTenantOfCurrentSite && (
                <Box sx={quickActionRowSx}>
                  <Typography variant="body2" sx={quickActionLabelSx}>
                    View
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    <Button component={Link} variant="contained" to={purchasePageUri()} sx={primaryActionButtonSx}>
                      Purchases
                    </Button>

                    {/* Keeps the role gate it had in the Spoof row, which this row does not wrap in. */}
                    <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
                      {showSpoofActions && (
                        <Button variant="contained" onClick={showCreditSecurePaymentDialog} sx={primaryActionButtonSx}>
                          Secure Credit Payment
                        </Button>
                      )}
                    </PermissionedComponent>

                    <PermissionedComponent
                      requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM, ROLE_TOUR_COORDINATOR]}
                    >
                      <PermissionedComponent
                        requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_TOUR_COORDINATOR]}
                      >
                        <Button
                          component={Link}
                          variant="outlined"
                          to={creditsPageUri(userCountry)}
                          disabled={!userCountry}
                          sx={actionButtonSx}
                        >
                          Credits
                        </Button>
                      </PermissionedComponent>

                      <PermissionedComponent
                        requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_EXPERIENCES_COORDINATOR]}
                      >
                        {shouldShowButton('referrals') && (
                          <Button variant="outlined" component={Link} to={referralLogPageUri()} sx={actionButtonSx}>
                            Referrals
                          </Button>
                        )}

                        {shouldShowButton('promoRequests') && (
                          <Button variant="outlined" component={Link} to={promoRequestPageUri()} sx={actionButtonSx}>
                            Promo Requests
                          </Button>
                        )}

                        {shouldShowButton('vouchers') && (
                          <Button variant="outlined" component={Link} to={voucherLookupPageUri()} sx={actionButtonSx}>
                            Vouchers
                          </Button>
                        )}
                      </PermissionedComponent>
                    </PermissionedComponent>
                  </Stack>
                </Box>
              )}

              {isTenantOfCurrentSite && (
                <Box sx={quickActionRowSx}>
                  <Typography variant="body2" sx={quickActionLabelSx}>
                    Comms
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    <PermissionedComponent
                      requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM, ROLE_TOUR_COORDINATOR]}
                    >
                      <PermissionedComponent
                        requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_EXPERIENCES_COORDINATOR]}
                      >
                        <Button variant="outlined" component={Link} to={newSentEmailUri()} sx={actionButtonSx}>
                          Sent Emails
                        </Button>
                        <Button variant="outlined" component={Link} to={newMarketingEmailsUri()} sx={actionButtonSx}>
                          Marketing Emails
                        </Button>
                      </PermissionedComponent>
                    </PermissionedComponent>
                    <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
                      <Tooltip title="Check suppression status and remove eligible email blocks">
                        <Button variant="outlined" onClick={showEmailStatusDialog} sx={actionButtonSx}>
                          Email Delivery Status
                        </Button>
                      </Tooltip>
                    </PermissionedComponent>
                  </Stack>
                </Box>
              )}

              {isTenantOfCurrentSite && (
                <Box sx={quickActionRowSx}>
                  <Typography variant="body2" sx={quickActionLabelSx}>
                    Create New
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    <PermissionedComponent
                      requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM, ROLE_TOUR_COORDINATOR]}
                    >
                      <PermissionedComponent
                        requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_EXPERIENCES_COORDINATOR]}
                      >
                        {shouldShowButton('newPromo') && (
                          <Button variant="outlined" onClick={showCreatePromoCodeModal} sx={actionButtonSx}>
                            Promo
                          </Button>
                        )}

                        {shouldShowButton('vouchers') && (
                          <Button variant="outlined" onClick={showQuickVoucherDialog} sx={actionButtonSx}>
                            Voucher
                          </Button>
                        )}
                      </PermissionedComponent>
                    </PermissionedComponent>

                    <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
                      <Button
                        variant="outlined"
                        component={Link}
                        to={newCustomOfferUri(user)}
                        data-testid="new-custom-offer-btn"
                        sx={actionButtonSx}
                      >
                        Custom Offer
                      </Button>
                      {shouldShowButton('createOfferLink') && (
                        <Tooltip
                          arrow
                          title="Create a link for a customer to purchase an offer that is currently offline / expired. This saves us having to edit the offer schedule and re-open it so just one customer to purchase it."
                        >
                          <Button
                            variant="outlined"
                            component={Link}
                            to={`/users/${user.id_member}/offer-deep-link`}
                            sx={actionButtonSx}
                          >
                            Offer Link
                          </Button>
                        </Tooltip>
                      )}
                    </PermissionedComponent>

                    <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
                      <Button
                        variant="outlined"
                        component={Link}
                        className="T-new-order-btn"
                        to={newOrderPageUri()}
                        sx={actionButtonSx}
                      >
                        Order
                      </Button>
                    </PermissionedComponent>

                    <PermissionedComponent
                      requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM, ROLE_TOUR_COORDINATOR]}
                    >
                      <PermissionedComponent
                        requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_EXPERIENCES_COORDINATOR]}
                      >
                        {window.configs.OFFLINE_FLIGHT_PURCHASE_ENABLED === 'on' && (
                          <Button
                            variant="outlined"
                            className="T-new-flight-btn"
                            onClick={showPromptToUseCustomOfferFlights}
                            sx={actionButtonSx}
                          >
                            Flight
                          </Button>
                        )}

                        {shouldShowButton('newGiftCard') && (
                          <Button
                            variant="outlined"
                            component={Link}
                            to={newGiftCardOrderPageUri()}
                            sx={actionButtonSx}
                          >
                            Gift Card
                          </Button>
                        )}

                        {shouldShowButton('newAtolEmail') && (
                          <Button variant="outlined" component={Link} to={newAtolPageUri()} sx={actionButtonSx}>
                            ATOL Email
                          </Button>
                        )}
                      </PermissionedComponent>
                    </PermissionedComponent>
                  </Stack>
                </Box>
              )}

              {isTenantOfCurrentSite && (
                <Box sx={quickActionRowSx}>
                  <Typography variant="body2" sx={quickActionLabelSx}>
                    Other
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
                      <Button variant="outlined" component={Link} to={historyUri(user)} sx={actionButtonSx}>
                        History
                      </Button>
                      <Button variant="outlined" component={Link} to={lesaChatsUri(user)} sx={actionButtonSx}>
                        LESA Chats
                      </Button>
                    </PermissionedComponent>
                    <UpgradeToLuxPlusButton
                      customer={user}
                      refreshData={refreshData}
                      isFullLuxPlusMember={checkCanRedeemLuxPlusBenefits(subscriptions)}
                      isFreePreviewMember={isFreePreviewLuxPlusMember(subscriptions)}
                    ></UpgradeToLuxPlusButton>
                  </Stack>
                </Box>
              )}
            </Stack>

            <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
              {!isTenantOfCurrentSite && shouldShowButton('createOfferLink') && (
                <Box sx={{ ...quickActionRowSx, mt: 2.25 }}>
                  <Typography variant="body2" sx={quickActionLabelSx}>
                    Create New
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    <Tooltip
                      arrow
                      title="Create a link for a customer to purchase an offer that is currently offline / expired. This saves us having to edit the offer schedule and re-open it so just one customer to purchase it."
                    >
                      <Button
                        variant="outlined"
                        component={Link}
                        to={`/users/${user.id_member}/offer-deep-link`}
                        sx={actionButtonSx}
                      >
                        Offer Link
                      </Button>
                    </Tooltip>
                  </Stack>
                </Box>
              )}
            </PermissionedComponent>

            <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
              <Divider sx={{ my: 2.5 }} />
              <Box sx={quickActionRowSx}>
                <Typography variant="body2" sx={{ ...quickActionLabelSx, color: 'error.main' }}>
                  Danger Zone
                </Typography>
                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                  {isTenantOfCurrentSite && (
                    <>
                      <Button variant="outlined" color="error" onClick={showForceLogoutModal} sx={destructiveButtonSx}>
                        Force Logout
                      </Button>

                      <PermissionedComponent>
                        <Button variant="outlined" color="error" onClick={gdprRemoval} sx={destructiveButtonSx}>
                          Delete Account
                        </Button>
                      </PermissionedComponent>

                      <Button variant="outlined" color="error" onClick={gdprRequest} sx={destructiveButtonSx}>
                        Request Account Deletion
                      </Button>
                    </>
                  )}

                  {canToggleAccountStatus && (
                    <Button
                      variant="outlined"
                      color={accountEnabled ? 'error' : 'success'}
                      onClick={() => setIsAccountStatusDialogOpen(true)}
                      disabled={isTogglingAccountStatus}
                      sx={
                        accountEnabled ? destructiveButtonSx : { ...destructiveButtonSx, borderColor: 'success.light' }
                      }
                    >
                      {accountEnabled ? 'Deactivate account' : 'Activate account'}
                    </Button>
                  )}

                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={showResetPasswordModal}
                    sx={neutralDangerButtonSx}
                  >
                    Reset Password
                  </Button>
                </Stack>
              </Box>
            </PermissionedComponent>
          </CardContent>
        </Card>

        <SamePhoneAccounts user={user} sx={{ mb: 0 }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '216px minmax(0, 1fr)' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <UserDetailsNavigation tabs={tabs} currentTab={currentTab} onChange={setCurrentTabHandler} />
          <Box
            ref={detailsPanelRef}
            component="section"
            aria-label={tabs[currentTab].label}
            sx={{ minWidth: 0, scrollMarginTop: 16 }}
          >
            {currentTab === 'account-history' && <UserAccountHistory user={user} tenancy={tenancy} />}
            {currentTab === 'custom-offers' && <CustomOffersTab user={user} />}
            {currentTab === 'quotes' && <QuotesTab user={user} />}
            {currentTab === 'loyalty-programs-memberships' && <FrequentFlyerMembershipsTab user={user} />}
            {currentTab === 'user-details' && (
              <>
                <UpdateUser
                  groupedSections
                  schema={schema.put}
                  brand={tenant.brand}
                  update={update}
                  user={user}
                  languageOptions={languageOptions}
                  initialBusinessDetails={initialBusinessDetails}
                  onBusinessDetailsSaved={handleBusinessDetailsSaved}
                  initialTravelPreferences={initialTravelPreferences}
                  isTravelPreferencesLoading={isTravelPreferencesLoading}
                  onTravelPreferencesSaved={handleTravelPreferencesSaved}
                  disallowedRoles={disallowedRoles}
                  ref={UpdateUserFormRef}
                  linkedAccountEmail={
                    tenant.brand === LE_BUSINESS_TRAVELLER ? (employee?.creditCustomerEmail ?? '') : undefined
                  }
                  linkedAccountId={tenant.brand === LE_BUSINESS_TRAVELLER ? employee?.creditCustomerId : undefined}
                />

                <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={2}
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 1,
                      bgcolor: 'background.paper',
                      borderTop: 1,
                      borderColor: 'divider',
                      py: 2,
                      mt: 3,
                    }}
                  >
                    <Button variant="contained" onClick={updateUser}>
                      Save changes
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Saves all user detail sections.
                    </Typography>
                  </Stack>
                </PermissionedComponent>
              </>
            )}

            {currentTab === 'luxplus-member-profile' && (
              <MembershipSubscriptionDetails
                subscriptions={subscriptions}
                customerId={user.id_member}
                currentSubscriptionOrderItem={currentSubscriptionOrderItem}
                currentSubscriptionOrder={currentSubscriptionOrder}
                refreshData={refreshData}
              />
            )}

            {currentTab === 'luxloyalty-member-profile' &&
              (luxLoyaltyAccount ? (
                <LuxLoyaltyUserProfile
                  account={luxLoyaltyAccount}
                  customerEmail={user.email}
                  refreshData={refreshData}
                  justEnrolledTier={justEnrolledTier}
                />
              ) : (
                <LuxLoyaltyEnrolPanel
                  userId={user.id_member}
                  userLabel={user.fullName || user.email || user.id_member}
                  onEnrolled={(tier) => {
                    setJustEnrolledTier(tier);
                    void refreshData();
                  }}
                />
              ))}

            {currentTab === 'velocity-memberships' && <VelocityMembershipsTab accountId={user.id_member} />}

            {currentTab === 'user-agency' && <UserAgencyDetails id={user.id_member}></UserAgencyDetails>}
            {currentTab === 'recent-searches' && (
              <UserRecentSearchTable memberId={user.memberId} memberEmail={user.email} tenant={tenant} />
            )}
            {currentTab === 'recent-offer-page-views' && <UserRecentOfferPageViewsTable userId={user.memberId} />}
            {currentTab === 'abandoned-carts' && (
              <UserAbandonedCartsTable
                userId={user.memberId}
                email={user.email}
                region={user.country_code}
                tenant={tenant}
              />
            )}
            {currentTab === 'carts' && <CartsTab userId={user.id_member} />}
            {currentTab === 'security' && <SecurityTab user={user} update={update} />}
            {currentTab === 'similar-accounts' && <SimilarAccountsTab user={user} />}
            {currentTab === 'atol-certificates' && <AtolCertificatesTab customerId={user.id_member} />}
          </Box>
        </Box>
        <Dialog
          open={pendingTab !== null}
          onClose={() => setPendingTab(null)}
          aria-labelledby="discard-user-draft-title"
        >
          <DialogTitle id="discard-user-draft-title">Discard unsaved changes?</DialogTitle>
          <DialogContent>
            Your changes to user details have not been saved. Switching sections will discard them.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingTab(null)}>Keep editing</Button>
            <Button
              variant="contained"
              onClick={() => {
                if (pendingTab !== null) {
                  changeCurrentTab(pendingTab);
                  setPendingTab(null);
                }
              }}
            >
              Discard changes
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={isQuickVoucherDialogOpen} onClose={hideQuickVoucherDialog} maxWidth="md" fullWidth scroll="paper">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 }}>
            Quick create voucher
            <IconButton aria-label="Close quick voucher dialog" onClick={hideQuickVoucherDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <SimpleVoucherForm
              mode="popup"
              prefillAssigneeUserId={user.id_member}
              prefillAssigneeUserLabel={user.fullName || user.email || ''}
            />
          </DialogContent>
        </Dialog>

        <CreatePromoForUserModal
          isModalOpen={isCreatePromoCodeModalVisible}
          closeModal={hideCreatePromoCodeModal}
          user={user}
        />

        <GDPRRemovalModal
          isGDPRRemovalModalVisible={isGDPRRemovalModalVisible}
          hideGDPRRemovalModal={hideGDPRRemovalModal}
          memberId={user.id_member}
          email={user.email}
          currencyCode={userCountry?.currencyCode}
          brand={tenant.brand}
        />
        <GDPRRequestModal
          isGDPRRequestModalVisible={isGDPRRequestModalVisible}
          hideGDPRRequestModal={hideGDPRRequestModal}
          memberId={user.id_member}
          email={user.email}
          currencyCode={userCountry?.currencyCode}
          brand={tenant.brand}
        />
        <ForceLogoutModal isOpen={isForceLogoutModalVisible} user={user} onClose={hideForceLogoutModal} />
        <Dialog
          open={isAccountStatusDialogOpen}
          onClose={() => !isTogglingAccountStatus && setIsAccountStatusDialogOpen(false)}
          aria-labelledby="account-status-dialog-title"
        >
          <DialogTitle id="account-status-dialog-title">
            {accountEnabled ? 'Deactivate account?' : 'Activate account?'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {accountEnabled
                ? `Deactivate ${user.email}? This will end their active sessions.`
                : `Activate ${user.email}? They will be able to sign in again.`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsAccountStatusDialogOpen(false)} disabled={isTogglingAccountStatus}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={accountEnabled ? 'error' : 'success'}
              onClick={toggleAccountStatus}
              disabled={isTogglingAccountStatus}
            >
              {isTogglingAccountStatus ? 'Saving...' : accountEnabled ? 'Deactivate account' : 'Activate account'}
            </Button>
          </DialogActions>
        </Dialog>
        {isResetPasswordModalVisible && (
          <ResetPasswordDialog key={user.id_member} user={user} onClose={hideResetPasswordModal} />
        )}
        <CreateCreditSecurePaymentDialog
          open={isCreditSecurePaymentDialogOpen}
          onClose={hideCreditSecurePaymentDialog}
          user={user}
          tenant={tenant}
        />
        <EmailStatusCard
          open={isEmailStatusDialogOpen}
          onClose={hideEmailStatusDialog}
          userId={user.id_member}
          brand={tenant.brand}
        />
      </Stack>
    </>
  );
}

export const UserDetailsComponent = UserDetails;

export default withTenant(UserDetails);
